//@flow
import React, {Fragment, useEffect, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import makeStyles from '@material-ui/core/styles/makeStyles';
import PropTypes from 'prop-types';
import {
  IconButton,
  Typography,
  Card,
  CardActions,
  Paper,
  Grid,
  CardContent,
  Hidden,
  Divider,
  Button,
} from '@material-ui/core';
import {Route, Switch} from 'react-router-dom';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import DoneIcon from '@material-ui/icons/Done';
import {colors} from '../../../assets/styles/colors';
import maskImage from '../../../assets/img/maskImage.png';
import HelpIcon from '@material-ui/icons/Help';
import PlayCircleFilledIcon from '@material-ui/icons/PlayCircleFilled';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import MyPayments from './MyPayments/MyPayments';
import AccountContainer from '../account/containers/AccountContainer';
import AlertDueDateTier from '../../tiers/components/AlertDueDateTier/AlertDueDateTier';
import {getTierName} from '../../store/utils/utils_tier';
import {getStringDate} from '../../../utils/date';
import {usePage} from '../../page/utils';

import type {Store, User, Subscription} from '../../../entities';
import {getBillingPortalLink} from '../../tiers/promises';
import {useToastActions} from '../../toast-alert/utils';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import {TIENDADA_LINKS} from '../../../constants';
import {useCurrentSubscription} from '../../tiers/utils';

type Props = {
  logout: () => void,
  store: Store,
  user: User,
  subscription?: Subscription,
  resetState: () => void,
  fetchCards: () => void,
};

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(3),
    marginTop: theme.spacing(5),
    boxShadow: '0px 4px 20px rgba(113, 113, 113, 0.15)',
    backgroundColor: 'white',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  fitPaper: {
    padding: theme.spacing(2, 0),
    margin: theme.spacing(4, 0, 0, 0),
    boxShadow: '0px 4px 20px rgba(113, 113, 113, 0.15)',
    backgroundColor: 'white',
    borderRadius: '12px',
    overflow: 'hidden',
    [theme.breakpoints.between('xs', 'md')]: {
      margin: theme.spacing(3, 0, 6, 0),
    },
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(3, 0, 2, 0),
    },
  },
  cardGoPayments: {
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    padding: theme.spacing(2, 0),
    marginTop: theme.spacing(3),
    margin: theme.spacing(1, 0, 1, 0),
    backgroundColor: 'white',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  title: {
    fontWeight: 600,
    margin: theme.spacing(2),
    textAlign: 'center',
  },
  content: {
    maxWidth: '600px',
    margin: 'auto',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(4),
    backgroundColor: '#F8F9FB',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
  },
  cardPlan: {
    borderRadius: '16px',
    backgroundColor: colors.purple3,
    backgroundImage: `url(${maskImage})`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
  },
  body: {
    color: 'white',
    alignItems: 'center',
    margin: 'auto',
  },
  tableContainer: {
    position: 'relative',
  },
  checkIcon: {
    borderRadius: '50%',
    background: 'linear-gradient(200.22deg, #02DB9A 9.9%, #00B382 88.75%)',
    color: 'white',
    padding: '3px',
    position: 'absolute',
    top: '0',
    right: '0',
  },
  linkPlanes: {
    textAlign: 'center',
    color: colors.purple3,
    borderRadius: '.75rem',
  },
  cardAction: {
    justifyContent: 'space-between',
    margin: theme.spacing(1, 1),
    padding: theme.spacing(0, 1),
  },
  divider: {
    margin: theme.spacing(0, 3),
    opacity: '0.3',
    backgroundColor: 'white',
  },
  dividerVertical: {
    backgroundColor: colors.grey300,
  },
  profileIcon: {
    display: 'flex',
    marginRight: '2px',
    padding: '0px 2px',
  },
  paymentIcon: {
    // marginRight: '2px',
    padding: '0px 2px',
  },
  manageSubscriptionButton: {
    borderRadius: '.75rem',
    textTransform: 'none',
  },
  rowGrid: {
    margin: '1rem 0',
  },
}));

export default usePage<Props>(Profile, 'Profile');

function Profile(props: Props) {
  const classes = useStyles();
  const {
    logout,
    store,
    user,
    resetState: resetAccountState,
    fetchCards,
  } = props;

  const subscription = useCurrentSubscription();

  const {showToast} = useToastActions();

  const [billingLink, setBillingLink] = useState('');

  const subscriptionEndDate = useMemo(() => {
    if (subscription) {
      return getStringDate(subscription.endDate);
      // if (subscription.state === 'PENDING')
      //   return getStringDate(subscription.paymentDueDate);
      // else return getStringDate(subscription.endDate);
    } else {
      return '';
    }
  }, [subscription]);

  useEffect(() => {
    if (!subscription) return;
    getBillingPortalLink(window.location.href)
      .then(response => {
        setBillingLink(response.url);
      })
      .catch(() => {
        showToast({
          message: 'Ocurrió un error al cargar los planes de TiendaDa',
          title: 'Error',
          type: 'danger',
        });
      });
  }, [showToast, subscription]);

  const cardGoPayments = (
    <Paper elevation={1} className={classes.cardGoPayments} hidden>
      <Grid container justify="center">
        <Grid item xs={10} sm={10} md={10}>
          <div style={{margin: 'auto'}}>
            <Typography variant="body1" color="primary">
              Ir a Mis Pagos
            </Typography>
          </div>
        </Grid>
        <Grid item xs={1} sm={1} md={1} style={{textAlign: 'end'}}>
          <Link to="/profile/my-payments" className={classes.paymentIcon}>
            <ArrowForwardIcon color="primary" />
          </Link>
        </Grid>
      </Grid>
    </Paper>
  );

  const cardButtonsSection = (
    <>
      <Paper elevation={1} className={classes.fitPaper}>
        <Grid container justify="center">
          <Grid item xs={6} sm={4} md={4} style={{display: 'flex'}}>
            <Button
              color="primary"
              startIcon={<PlayCircleFilledIcon />}
              style={{margin: 'auto'}}
              href={TIENDADA_LINKS.youtube.main_channel}
              target="_blank"
              rel="noopener noreferrer"
            >
              Tutorial
            </Button>
          </Grid>

          <Grid item xs={6} sm={4} md={4} style={{display: 'flex'}}>
            <Divider
              orientation="vertical"
              className={classes.dividerVertical}
            />
            <Button
              color="primary"
              startIcon={<HelpIcon />}
              style={{margin: 'auto'}}
              target="_blank"
              rel="noopener noreferrer"
              href="https://api.whatsapp.com/send?phone=51972076020&text=Hola!%20Quisiera%20contactarme%20con%20TiendaDa."
            >
              Ayuda
            </Button>
          </Grid>
          <Hidden xsDown>
            <Grid item sm={4} md={4} style={{display: 'flex'}}>
              <Divider
                orientation="vertical"
                className={classes.dividerVertical}
              />
              <Button
                style={{margin: 'auto', color: `${colors.error}`}}
                startIcon={<ExitToAppIcon />}
                onClick={() => logout()}
              >
                Cerrar sesión
              </Button>
            </Grid>
          </Hidden>
        </Grid>
      </Paper>

      <Hidden smUp>
        <Grid container justify="center">
          <Grid
            item
            xs={10}
            sm={10}
            style={{marginBottom: '70px', display: 'flex'}}
          >
            <Button
              style={{margin: 'auto', color: `${colors.error}`}}
              startIcon={<ExitToAppIcon />}
              onClick={() => logout()}
            >
              Cerrar sesión
            </Button>
          </Grid>
        </Grid>
      </Hidden>
    </>
  );

  useEffect(() => () => resetAccountState(), [resetAccountState]);

  useEffect(() => {
    if (user && user.type === 'vendor-user') fetchCards();
  }, [fetchCards, user]);

  return (
    <Fragment>
      <AlertDueDateTier />

      <Switch>
        <Route exact path="/profile">
          <div className={classes.content}>
            <Paper className={classes.paper}>
              <Typography component="h5" variant="h5" className={classes.title}>
                {user.firstName && user.firstName}
                {user.lastName && ` ${user.lastName}`}
              </Typography>
              {user && user.type === 'vendor-user' && (
                <Grid container justify="center">
                  <Grid item xs={12} sm={12} md={12}>
                    <Card className={classes.cardPlan}>
                      <CardContent>
                        <Grid
                          container
                          justify="center"
                          className={classes.tableContainer}
                        >
                          <Hidden mdUp>
                            <Grid item xs={12}>
                              <Typography className={classes.body} align="left">
                                Plan actual
                              </Typography>
                            </Grid>
                            <Grid item xs={12} style={{display: 'flex'}}>
                              <Typography
                                component="h5"
                                variant="h5"
                                className={classes.body}
                                align="center"
                              >
                                {getTierName(store)}
                              </Typography>
                            </Grid>
                          </Hidden>
                          <Hidden smDown>
                            <Grid item xs={8} style={{paddingLeft: '8px'}}>
                              <Typography className={classes.body} align="left">
                                Plan actual
                              </Typography>
                              <Typography
                                component="h5"
                                variant="h5"
                                className={classes.body}
                                align="left"
                              >
                                {getTierName(store)}
                              </Typography>
                            </Grid>
                          </Hidden>

                          {subscription && (
                            <Grid item xs={8} style={{paddingLeft: '8px'}}>
                              <Typography
                                component="h6"
                                variant="h6"
                                className={classes.body}
                                align="left"
                              >
                                {subscription?.subscriptionPrice}
                              </Typography>
                            </Grid>
                          )}

                          <DoneIcon className={classes.checkIcon} />
                        </Grid>
                      </CardContent>
                      <Divider className={classes.divider} />
                      <CardActions className={classes.cardAction}>
                        {subscriptionEndDate && (
                          <Typography
                            variant="body1"
                            className={classes.body}
                            align="left"
                            style={{marginLeft: '10px'}}
                          >
                            {`Válido hasta el ${subscriptionEndDate}`}
                          </Typography>
                        )}
                        {billingLink && (
                          <>
                            <Typography
                              variant="body1"
                              style={{marginRight: 'inherit'}}
                              className={classes.body}
                              align="center"
                            >
                              Mejorar Plan
                            </Typography>
                            <IconButton
                              variant="contained"
                              size="small"
                              component={Link}
                              to={billingLink}
                              className={classes.profileIcon}
                            >
                              <ArrowForwardIcon style={{color: 'white'}} />
                            </IconButton>
                          </>
                        )}
                      </CardActions>
                    </Card>
                  </Grid>
                  <Grid
                    container
                    justify="center"
                    item
                    xs={10}
                    sm={10}
                    md={10}
                    className={classes.rowGrid}
                  >
                    {!subscription && (
                      <CustomButton
                        className={classes.linkPlanes}
                        variant="outlined"
                        to="/tiers-list"
                      >
                        Ver Planes
                      </CustomButton>
                    )}
                    {subscription && (
                      <CustomButton
                        variant="contained"
                        color="primary"
                        component="a"
                        href={billingLink ?? '/tiers-list'}
                        className={classes.manageSubscriptionButton}
                      >
                        Gestionar mi suscripción
                      </CustomButton>
                    )}
                  </Grid>
                </Grid>
              )}
              <Hidden smDown>
                {user && user.type === 'vendor-user' && cardGoPayments}
                {store && <AccountContainer />}
              </Hidden>
            </Paper>

            <Hidden mdUp>
              {user && user.type === 'vendor-user' && cardGoPayments}
              {store && <AccountContainer />}
            </Hidden>

            {cardButtonsSection}
          </div>
        </Route>

        <Route exact path="/profile/my-payments">
          <MyPayments />
        </Route>
      </Switch>
    </Fragment>
  );
}

Profile.propTypes = {
  logout: PropTypes.func.isRequired,
  user: PropTypes.object,
  store: PropTypes.object,
  subscription: PropTypes.object,
  resetState: PropTypes.func.isRequired,
  fetchCards: PropTypes.func.isRequired,
};
